import type OverflowList from './OverflowList.js'
import { html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import './OverflowList.js'
import '../visually-hidden/VisuallyHidden.js'

/**
 * Displays the number of items currently hidden by the nearest Overflow List.
 *
 * @status draft
 * @category layout
 * @parent OverflowList
 * @lightDom true
 *
 * @localization itemSingular - Default noun used when exactly one item is hidden.
 * @localization itemPlural - Default noun used when multiple items are hidden.
 * @localization visualMore - Expanded visual count, given a locale-formatted number.
 * @localization visualCompact - Compact visual count, given a locale-formatted number.
 * @localization accessibleMore - Accessible count phrase, given a locale-formatted number and noun.
 */
@customElement('nord-overflow-list-count')
export default class OverflowListCount extends DraftComponentMixin(LitElement) {
  /** Shows only the signed count visually while keeping the full accessible phrase. */
  @property({ reflect: true, type: Boolean }) compact = false

  /** Noun used by the accessible phrase when exactly one item is hidden. */
  @property({ attribute: 'singular-label' }) singularLabel?: string

  /** Noun used by the accessible phrase when multiple items are hidden. */
  @property({ attribute: 'plural-label' }) pluralLabel?: string

  @state() private overflowCount = 0
  private list?: OverflowList
  private localize = new LocalizeController<'nord-overflow-list-count'>(this)

  connectedCallback() {
    super.connectedCallback()
    this.bindList()
  }

  disconnectedCallback() {
    this.unbindList()
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }

  protected render() {
    const count = this.formatCount(this.overflowCount)
    const itemLabel = this.overflowCount === 1
      ? this.singularLabel?.trim() || this.localize.term('itemSingular')
      : this.pluralLabel?.trim() || this.localize.term('itemPlural')
    const visual = this.compact
      ? this.localize.term('visualCompact', count)
      : this.localize.term('visualMore', count)
    const accessible = this.localize.term('accessibleMore', count, itemLabel)

    return html`
      <span aria-hidden="true">${visual}</span>
      <nord-visually-hidden>${accessible}</nord-visually-hidden>
    `
  }

  protected updated() {
    const count = this.formatCount(this.overflowCount)
    const itemLabel = this.overflowCount === 1
      ? this.singularLabel?.trim() || this.localize.term('itemSingular')
      : this.pluralLabel?.trim() || this.localize.term('itemPlural')
    this.setAttribute('aria-label', this.localize.term('accessibleMore', count, itemLabel))
  }

  private bindList() {
    const list = this.closest<OverflowList>('nord-overflow-list') ?? undefined
    if (list === this.list) {
      return
    }
    this.unbindList()
    this.list = list
    this.list?.addEventListener('nord-overflow-change', this.handleOverflowChange)
    this.overflowCount = this.list?.overflowItems.length ?? 0
  }

  private unbindList() {
    this.list?.removeEventListener('nord-overflow-change', this.handleOverflowChange)
    this.list = undefined
  }

  private handleOverflowChange = (event: Event) => {
    if (event.target !== this.list) {
      return
    }
    const overflowItems = (event as Event & { overflowItems?: readonly unknown[] }).overflowItems
    this.overflowCount = overflowItems?.length ?? this.list.overflowItems.length
  }

  private formatCount(count: number) {
    const locale = this.localize.lang || this.localize.resolvedLang || 'en-US'
    try {
      return new Intl.NumberFormat(locale).format(count)
    }
    catch {
      return new Intl.NumberFormat('en-US').format(count)
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-overflow-list-count': OverflowListCount
  }
}
